import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Database,
  History,
  RefreshCw,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react';
import { useToast } from '../../Common/context/ToastContext';
import {
  applyGnpShinsungSyncBatch,
  cancelGnpShinsungSyncBatch,
  createGnpShinsungSyncPreview,
  getGnpShinsungSyncBatch,
  getGnpShinsungSyncBatches,
} from '../api/gnpShinsungSync';

const HISTORY_LIMIT = 20;
const DETAIL_LIMIT = 100;

const STATUS_LABELS = {
  PREVIEWED: '검토 대기',
  APPLIED: '적용 완료',
  CANCELED: '취소',
  EXPIRED: '만료',
  FAILED: '실패',
};

const ACTION_LABELS = {
  NEW_TERMINAL: '신규 담당자',
  UPDATE_TERMINAL: '담당자 정보/상태 변경',
  NEW_PHARMACY: '신규 거래처',
  UPDATE_PHARMACY: '거래처 정보/상태 변경',
  NEW_MAPPING: '신규 담당자 배정',
  REASSIGN_MAPPING: '담당자 변경',
  UPDATE_MAPPING_STATUS: '배정 상태 변경',
  NO_CHANGE: '변경 없음',
};

const ACTION_OPTIONS = Object.entries(ACTION_LABELS);

const SUMMARY_ITEMS = [
  ['newTerminals', '신규 담당자'],
  ['updatedTerminals', '담당자 변경'],
  ['newPharmacies', '신규 거래처'],
  ['updatedPharmacies', '거래처 변경'],
  ['reassignedMappings', '담당자 재배정'],
  ['updatedMappingStatuses', '상태 변경'],
  ['unchangedRows', '변경 없음'],
  ['warnings', '경고'],
];

function firstValue(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '');
}

function asNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeSummary(summary = {}) {
  return {
    sourceRows: asNumber(firstValue(summary.sourceRows, summary.source_rows)),
    sourceTerminals: asNumber(firstValue(summary.sourceTerminals, summary.source_terminals)),
    activeSourceRows: asNumber(firstValue(summary.activeSourceRows, summary.active_source_rows)),
    inactiveSourceRows: asNumber(firstValue(summary.inactiveSourceRows, summary.inactive_source_rows)),
    newTerminals: asNumber(firstValue(summary.newTerminals, summary.new_terminals)),
    updatedTerminals: asNumber(firstValue(summary.updatedTerminals, summary.updated_terminals)),
    newPharmacies: asNumber(firstValue(summary.newPharmacies, summary.new_pharmacies)),
    updatedPharmacies: asNumber(firstValue(summary.updatedPharmacies, summary.updated_pharmacies)),
    newMappings: asNumber(firstValue(summary.newMappings, summary.new_mappings)),
    reassignedMappings: asNumber(firstValue(summary.reassignedMappings, summary.reassigned_mappings)),
    updatedMappingStatuses: asNumber(firstValue(summary.updatedMappingStatuses, summary.updated_mapping_statuses)),
    changedRows: asNumber(firstValue(summary.changedRows, summary.changed_rows)),
    unchangedRows: asNumber(firstValue(summary.unchangedRows, summary.unchanged_rows)),
    warnings: asNumber(summary.warnings),
  };
}

function normalizeBatch(batch = {}) {
  const cp = batch.cp ?? {};
  const creator = batch.createdAdmin ?? batch.created_admin ?? {};
  const applier = batch.appliedAdmin ?? batch.applied_admin ?? {};
  return {
    batchId: firstValue(batch.batchId, batch.batch_id, batch.id),
    status: String(batch.status || '').toUpperCase(),
    cp: {
      id: firstValue(cp.id, batch.cpId, batch.cp_id),
      cpId: firstValue(cp.cpId, cp.cp_id, batch.cpCode, batch.cp_code),
      name: firstValue(cp.name, batch.cpName, batch.cp_name),
    },
    sourceRowCount: asNumber(firstValue(batch.sourceRowCount, batch.source_row_count)),
    sourceFetchedAt: firstValue(batch.sourceFetchedAt, batch.source_fetched_at),
    expiresAt: firstValue(batch.expiresAt, batch.expires_at),
    createdAt: firstValue(batch.createdAt, batch.created_at),
    createdBy: firstValue(
      batch.createdByName,
      batch.created_by_name,
      batch.createdBy,
      batch.created_by,
      creator.name,
      creator.adminId,
      creator.admin_id,
    ),
    appliedAt: firstValue(batch.appliedAt, batch.applied_at),
    appliedBy: firstValue(
      batch.appliedByName,
      batch.applied_by_name,
      batch.appliedBy,
      batch.applied_by,
      applier.name,
      applier.adminId,
      applier.admin_id,
    ),
    summary: normalizeSummary(batch.summary),
  };
}

function normalizeActions(row = {}) {
  const raw = row.plannedActions ?? row.planned_actions ?? [];
  const list = Array.isArray(raw) ? raw : String(raw).split(/[,|]/);
  return list
    .map((value) => String(value?.action ?? value?.type ?? value ?? '').trim().toUpperCase())
    .filter(Boolean);
}

function normalizeDetailRow(row = {}, index = 0) {
  return {
    ...row,
    key: firstValue(row.id, row.rowNo, row.row_no, index),
    rowNo: firstValue(row.rowNo, row.row_no, index + 1),
    customerCode: firstValue(row.customerCode, row.customer_code, '-'),
    customerName: firstValue(row.customerName, row.customer_name, '-'),
    businessNo: firstValue(row.businessNo, row.business_no, '-'),
    managerSourceId: firstValue(row.managerSourceId, row.manager_source_id, '-'),
    managerCode: firstValue(row.managerCode, row.manager_code, '-'),
    managerName: firstValue(row.managerName, row.manager_name, '-'),
    sourceStatus: String(firstValue(row.sourceStatus, row.source_status, '')).toUpperCase(),
    previousManagerSourceId: firstValue(row.previousManagerSourceId, row.previous_manager_source_id, '-'),
    previousManagerName: firstValue(row.previousManagerName, row.previous_manager_name, '-'),
    warningMessage: firstValue(row.warningMessage, row.warning_message, ''),
    plannedActions: normalizeActions(row),
  };
}

function parseServerDate(value) {
  if (!value) return null;
  const raw = String(value).trim();
  const normalized = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw)
    ? `${raw.replace(' ', 'T')}Z`
    : raw;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatKst(value) {
  if (!value) return '-';
  const date = parseServerDate(value);
  if (!date) return String(value);
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

function statusTone(status) {
  if (status === 'APPLIED') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (status === 'PREVIEWED') return 'bg-blue-50 text-blue-700 border-blue-200';
  if (status === 'FAILED') return 'bg-red-50 text-red-700 border-red-200';
  if (status === 'EXPIRED') return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-slate-100 text-slate-600 border-slate-200';
}

function actionTone(action) {
  if (action.startsWith('NEW_')) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
  if (action === 'REASSIGN_MAPPING') return 'bg-amber-50 text-amber-700 border-amber-200';
  if (action === 'NO_CHANGE') return 'bg-slate-100 text-slate-500 border-slate-200';
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-bold ${statusTone(status)}`}>
      {STATUS_LABELS[status] || status || '-'}
    </span>
  );
}

function ActionBadges({ actions }) {
  if (!actions.length) return <span className="text-[#8b95a1]">-</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {actions.map((action) => (
        <span key={action} className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold ${actionTone(action)}`}>
          {ACTION_LABELS[action] || action}
        </span>
      ))}
    </div>
  );
}

function Modal({ children, onClose, width = 'max-w-lg' }) {
  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-4"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}
    >
      <div className={`max-h-[90vh] w-full overflow-auto rounded-2xl bg-white p-6 shadow-2xl ${width}`}>{children}</div>
    </div>
  );
}

function ConfirmationModal({ mode, batch, working, onClose, onConfirm }) {
  const apply = mode === 'apply';
  const summary = batch?.summary ?? normalizeSummary();
  return (
    <Modal onClose={() => { if (!working) onClose(); }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[20px] font-extrabold text-[#191f28]">{apply ? '동기화 최종 승인' : '미리보기 취소'}</h2>
          <p className="mt-1 text-[12px] text-[#8b95a1]">Batch #{batch?.batchId}</p>
        </div>
        <button type="button" onClick={onClose} disabled={working} className="rounded-lg p-2 text-[#8b95a1] hover:bg-[#f2f4f6] disabled:opacity-40" aria-label="닫기"><X size={20} /></button>
      </div>

      {apply ? (
        <>
          <div className="mt-5 grid grid-cols-2 gap-2 text-[12px]">
            {[
              ['신규 담당자', summary.newTerminals],
              ['변경 담당자', summary.updatedTerminals],
              ['신규 거래처', summary.newPharmacies],
              ['변경 거래처', summary.updatedPharmacies],
              ['담당자 재배정', summary.reassignedMappings],
              ['활성/비활성 변경', summary.updatedMappingStatuses],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between rounded-lg bg-[#f7f8fa] px-3 py-2.5"><span className="text-[#6b7684]">{label}</span><strong>{asNumber(value).toLocaleString('ko-KR')}건</strong></div>
            ))}
          </div>
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-semibold leading-5 text-amber-800">
            승인하면 현재 미리보기 내용이 실제 데이터에 반영됩니다.
          </div>
        </>
      ) : (
        <div className="mt-5 rounded-xl border border-[#e5e8eb] bg-[#f9fafb] px-4 py-4 text-[13px] leading-6 text-[#4e5968]">
          이 미리보기를 취소하시겠습니까? 취소해도 담당자·약국·매핑 데이터는 변경되지 않습니다.
        </div>
      )}

      <div className="mt-5 flex gap-2">
        <button type="button" onClick={onClose} disabled={working} className="flex-1 rounded-xl border border-[#dfe3e8] py-3 text-[13px] font-bold text-[#4e5968] disabled:opacity-40">닫기</button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={working}
          className={`flex-1 rounded-xl py-3 text-[13px] font-bold text-white disabled:opacity-40 ${apply ? 'bg-[#191f28]' : 'bg-red-600'}`}
        >
          {working ? '처리 중...' : apply ? '최종 승인' : '미리보기 취소'}
        </button>
      </div>
    </Modal>
  );
}

export default function GnpShinsungSync() {
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const [historyRows, setHistoryRows] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyStatus, setHistoryStatus] = useState('');
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const [activeBatch, setActiveBatch] = useState(null);
  const [detailRows, setDetailRows] = useState([]);
  const [detailTotal, setDetailTotal] = useState(0);
  const [detailPage, setDetailPage] = useState(1);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailRefreshKey, setDetailRefreshKey] = useState(0);

  const [searchDraft, setSearchDraft] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [action, setAction] = useState('');
  const [includeNoChange, setIncludeNoChange] = useState(false);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [mutation, setMutation] = useState(null);
  const [confirmMode, setConfirmMode] = useState(null);
  const [conflictMessage, setConflictMessage] = useState('');
  const [now, setNow] = useState(Date.now());

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await getGnpShinsungSyncBatches({
        page: historyPage,
        limit: HISTORY_LIMIT,
        ...(historyStatus ? { status: historyStatus } : {}),
      });
      const rows = Array.isArray(data?.rows) ? data.rows.map(normalizeBatch) : [];
      setHistoryRows(rows);
      setHistoryTotal(asNumber(data?.total ?? rows.length));
    } catch (error) {
      setHistoryRows([]);
      setHistoryTotal(0);
      showToastRef.current(error?.message || '동기화 이력을 불러오지 못했습니다.', 'error');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage, historyStatus]);

  useEffect(() => { loadHistory(); }, [loadHistory, historyRefreshKey]);

  const activeBatchId = activeBatch?.batchId;
  const loadDetail = useCallback(async () => {
    if (!activeBatchId) {
      setDetailRows([]);
      setDetailTotal(0);
      return;
    }
    setDetailLoading(true);
    try {
      const data = await getGnpShinsungSyncBatch(activeBatchId, {
        page: detailPage,
        limit: DETAIL_LIMIT,
        ...(searchQuery ? { q: searchQuery } : {}),
        ...(action ? { action } : {}),
        changeOnly: includeNoChange ? false : true,
      });
      const sourceBatch = data?.batch ?? data?.batchInfo ?? data;
      const normalized = normalizeBatch(sourceBatch);
      setActiveBatch((current) => ({
        ...current,
        batchId: normalized.batchId ?? current?.batchId,
        status: normalized.status || current?.status,
        cp: normalized.cp?.name || normalized.cp?.cpId ? normalized.cp : current?.cp,
        sourceRowCount: firstValue(sourceBatch?.sourceRowCount, sourceBatch?.source_row_count) !== undefined
          ? normalized.sourceRowCount
          : current?.sourceRowCount,
        sourceFetchedAt: normalized.sourceFetchedAt ?? current?.sourceFetchedAt,
        expiresAt: normalized.expiresAt ?? current?.expiresAt,
        createdAt: normalized.createdAt ?? current?.createdAt,
        createdBy: normalized.createdBy ?? current?.createdBy,
        appliedAt: normalized.appliedAt ?? current?.appliedAt,
        appliedBy: normalized.appliedBy ?? current?.appliedBy,
        summary: sourceBatch?.summary ? normalized.summary : current?.summary,
      }));
      const responseRows = data?.rows ?? data?.changes?.rows ?? data?.details?.rows ?? [];
      setDetailRows(Array.isArray(responseRows) ? responseRows.map(normalizeDetailRow) : []);
      setDetailTotal(asNumber(data?.total ?? data?.changes?.total ?? data?.details?.total ?? responseRows.length));
    } catch (error) {
      setDetailRows([]);
      setDetailTotal(0);
      showToastRef.current(error?.message || '배치 상세를 불러오지 못했습니다.', 'error');
    } finally {
      setDetailLoading(false);
    }
  }, [activeBatchId, detailPage, searchQuery, action, includeNoChange]);

  useEffect(() => { loadDetail(); }, [loadDetail, detailRefreshKey]);

  useEffect(() => {
    if (!activeBatch || activeBatch.status !== 'PREVIEWED') return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, [activeBatch]);

  const newestBatch = historyRows[0] ?? null;
  const historyLastPage = Math.max(1, Math.ceil(historyTotal / HISTORY_LIMIT));
  const detailLastPage = Math.max(1, Math.ceil(detailTotal / DETAIL_LIMIT));
  const expiresAtMs = parseServerDate(activeBatch?.expiresAt)?.getTime() ?? Number.NaN;
  const expired = Number.isFinite(expiresAtMs) && expiresAtMs <= now;
  const batchIsPreviewed = activeBatch?.status === 'PREVIEWED';
  const canApply = batchIsPreviewed && !expired && mutation === null;
  const canCancel = batchIsPreviewed && mutation === null;
  const summary = activeBatch?.summary ?? normalizeSummary();

  const selectBatch = (batch) => {
    setActiveBatch(batch);
    setDetailPage(1);
    setSearchDraft('');
    setSearchQuery('');
    setAction('');
    setIncludeNoChange(false);
    setConflictMessage('');
  };

  const createPreview = async () => {
    if (previewLoading) return;
    setPreviewLoading(true);
    setConflictMessage('');
    try {
      const data = await createGnpShinsungSyncPreview();
      const batch = normalizeBatch(data);
      setActiveBatch(batch);
      setDetailPage(1);
      setSearchDraft('');
      setSearchQuery('');
      setAction('');
      setIncludeNoChange(false);
      setHistoryPage(1);
      setHistoryStatus('');
      setHistoryRefreshKey((value) => value + 1);
      showToastRef.current('신성 데이터 미리보기가 생성되었습니다. 변경사항을 검토해 주세요.');
    } catch (error) {
      showToastRef.current(error?.message || '미리보기 생성에 실패했습니다.', 'error');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleMutation = async () => {
    if (!activeBatchId || !confirmMode || mutation) return;
    const mode = confirmMode;
    setMutation(mode);
    setConflictMessage('');
    try {
      const data = mode === 'apply'
        ? await applyGnpShinsungSyncBatch(activeBatchId)
        : await cancelGnpShinsungSyncBatch(activeBatchId);
      const returned = data?.batch ?? data;
      setActiveBatch((current) => {
        const normalized = returned && typeof returned === 'object' ? normalizeBatch(returned) : {};
        return {
          ...current,
          batchId: normalized.batchId ?? current?.batchId,
          status: String(normalized.status || (mode === 'apply' ? 'APPLIED' : 'CANCELED')).toUpperCase(),
          cp: returned?.cp ? normalized.cp : current?.cp,
          sourceRowCount: firstValue(returned?.sourceRowCount, returned?.source_row_count) !== undefined ? normalized.sourceRowCount : current?.sourceRowCount,
          sourceFetchedAt: normalized.sourceFetchedAt ?? current?.sourceFetchedAt,
          expiresAt: normalized.expiresAt ?? current?.expiresAt,
          appliedAt: normalized.appliedAt ?? current?.appliedAt,
          appliedBy: normalized.appliedBy ?? current?.appliedBy,
          summary: returned?.summary ? normalized.summary : current?.summary,
        };
      });
      setConfirmMode(null);
      setHistoryRefreshKey((value) => value + 1);
      setDetailRefreshKey((value) => value + 1);
      showToastRef.current(mode === 'apply' ? '신성 담당자·약국 데이터가 적용되었습니다.' : '미리보기가 취소되었습니다.');
    } catch (error) {
      const code = Number(error?.code ?? error?.response?.data?.code);
      const message = error?.message || error?.response?.data?.msg || '요청 처리에 실패했습니다.';
      if (code === 4090) {
        setConflictMessage(`${message} 기존 배치를 처리하지 말고 새 미리보기를 생성해 주세요.`);
        setConfirmMode(null);
        setHistoryRefreshKey((value) => value + 1);
        setDetailRefreshKey((value) => value + 1);
      }
      showToastRef.current(message, 'error');
    } finally {
      setMutation(null);
    }
  };

  const submitDetailSearch = (event) => {
    event.preventDefault();
    setDetailPage(1);
    setSearchQuery(searchDraft.trim());
  };

  return (
    <div className="flex-1 min-h-0 overflow-y-auto bg-[#f6f9f7] px-5 py-5 lg:px-7">
      <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]"><Database size={19} /></span>
              <div>
                <h1 className="text-[23px] font-extrabold text-[#191f28]">신성 담당자/약국 동기화</h1>
                <p className="mt-0.5 text-[12px] text-[#6b7684]">신성 MS-SQL 원천 데이터를 검토한 후 {process.env.REACT_APP_SHINSUNG_SYNC_TARGET_NAME || 'GNP 인천약품'} 데이터에 반영합니다.</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={createPreview}
            disabled={previewLoading || mutation !== null}
            className="flex h-10 items-center gap-2 rounded-xl bg-[var(--brand-primary)] px-4 text-[13px] font-bold text-white shadow-sm hover:bg-[var(--brand-primary-hover)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw size={16} className={previewLoading ? 'animate-spin' : ''} />
            {previewLoading ? '신성 데이터 불러오는 중...' : '신성 데이터 불러오기'}
          </button>
        </header>

        <section className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <div className="rounded-xl border border-[#dfe5e1] bg-white p-4 lg:col-span-2">
            <div className="flex items-center gap-2 text-[12px] font-bold text-[#6b7684]"><History size={15} />마지막 동기화 상태</div>
            {newestBatch ? (
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px]">
                <StatusBadge status={newestBatch.status} />
                <span><strong className="text-[#191f28]">Batch #{newestBatch.batchId}</strong></span>
                <span className="text-[#6b7684]">생성 {formatKst(newestBatch.createdAt)}</span>
                <span className="text-[#6b7684]">생성 관리자 {newestBatch.createdBy || '-'}</span>
                {newestBatch.appliedAt && <span className="text-[#6b7684]">적용 {formatKst(newestBatch.appliedAt)}</span>}
              </div>
            ) : <p className="mt-3 text-[12px] text-[#8b95a1]">동기화 이력이 없습니다.</p>}
          </div>
          <div className="rounded-xl border border-[#dfe5e1] bg-white p-4">
            <div className="flex items-center gap-2 text-[12px] font-bold text-[#6b7684]"><ShieldCheck size={15} />적용 대상</div>
            <p className="mt-3 text-[14px] font-extrabold text-[#191f28]">인천약품 단일 CP</p>
            <p className="mt-1 text-[11px] text-[#8b95a1]">기존 관리자 세션 인증 사용 · 별도 OTP 없음</p>
          </div>
        </section>

        {conflictMessage && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">
            <span className="flex items-center gap-2 font-semibold"><AlertTriangle size={17} />{conflictMessage}</span>
            <button type="button" onClick={createPreview} disabled={previewLoading} className="rounded-lg bg-red-600 px-3 py-2 font-bold text-white disabled:opacity-50">새 미리보기 생성</button>
          </div>
        )}

        {activeBatch && (
          <section className="overflow-hidden rounded-xl border border-[#dfe5e1] bg-white">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#eef1f4] px-5 py-4">
              <div>
                <div className="flex flex-wrap items-center gap-2"><h2 className="text-[17px] font-extrabold text-[#191f28]">Batch #{activeBatch.batchId} 미리보기</h2><StatusBadge status={expired && batchIsPreviewed ? 'EXPIRED' : activeBatch.status} /></div>
                <p className="mt-1 text-[11px] text-[#8b95a1]">
                  원천 조회 {formatKst(activeBatch.sourceFetchedAt)} · 만료 {formatKst(activeBatch.expiresAt)} KST · 원천 {asNumber(activeBatch.sourceRowCount || summary.sourceRows).toLocaleString('ko-KR')}행
                </p>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setConfirmMode('cancel')} disabled={!canCancel} className="rounded-lg border border-red-200 px-4 py-2 text-[12px] font-bold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40">미리보기 취소</button>
                <button type="button" onClick={() => setConfirmMode('apply')} disabled={!canApply} className="flex items-center gap-1.5 rounded-lg bg-[#191f28] px-4 py-2 text-[12px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"><CheckCircle2 size={15} />최종 승인</button>
              </div>
            </div>

            {expired && batchIsPreviewed && <div className="mx-5 mt-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-800"><Clock3 size={16} />미리보기가 만료되어 승인할 수 없습니다. 새 미리보기를 생성해 주세요.</div>}

            <div className="grid grid-cols-2 gap-2 p-5 sm:grid-cols-4 xl:grid-cols-8">
              {SUMMARY_ITEMS.map(([key, label]) => (
                <div key={key} className={`rounded-xl border px-3 py-3 ${key === 'warnings' && summary[key] > 0 ? 'border-amber-200 bg-amber-50' : 'border-[#e8ebed] bg-[#fafbfb]'}`}>
                  <span className="block text-[11px] text-[#6b7684]">{label}</span>
                  <strong className="mt-1 block text-[19px] font-extrabold text-[#191f28]">{asNumber(summary[key]).toLocaleString('ko-KR')}</strong>
                </div>
              ))}
            </div>

            <div className="border-t border-[#eef1f4] px-5 py-4">
              <form onSubmit={submitDetailSearch} className="flex flex-wrap items-end gap-2">
                <label className="min-w-[300px] flex-1 text-[11px] font-semibold text-[#6b7684]">검색어<span className="mt-1 flex h-9 items-center gap-2 rounded-lg border border-[#dfe3e8] px-3 focus-within:border-[var(--brand-primary)]"><Search size={15} className="text-[#8b95a1]" /><input value={searchDraft} onChange={(event) => setSearchDraft(event.target.value)} placeholder="거래처코드·명, 사업자번호, 담당자 ID·코드·이름" className="min-w-0 flex-1 border-0 bg-transparent text-[12px] outline-none" /></span></label>
                <label className="text-[11px] font-semibold text-[#6b7684]">변경 유형<select value={action} onChange={(event) => { setAction(event.target.value); setDetailPage(1); }} className="mt-1 block h-9 min-w-[190px] rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px]"><option value="">전체</option>{ACTION_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                <label className="flex h-9 items-center gap-2 rounded-lg border border-[#dfe3e8] bg-white px-3 text-[12px] font-semibold text-[#4e5968]"><input type="checkbox" checked={includeNoChange} onChange={(event) => { setIncludeNoChange(event.target.checked); setDetailPage(1); }} className="h-4 w-4 accent-[var(--brand-primary)]" />변경 없음 포함</label>
                <button type="submit" className="flex h-9 items-center gap-1.5 rounded-lg bg-[var(--brand-primary)] px-4 text-[12px] font-bold text-white"><Search size={14} />조회</button>
              </form>
            </div>

            <div className="max-h-[520px] min-h-[280px] overflow-auto border-t border-[#eef1f4]">
              <table className="w-full min-w-[1320px] table-fixed text-left text-[11px]">
                <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]"><tr className="h-10 border-b border-[#e5e8eb]"><th className="w-[8%] px-3">거래처코드</th><th className="w-[13%] px-3">거래처명</th><th className="w-[7%] px-3">상태</th><th className="w-[12%] px-3">기존 담당자</th><th className="w-[11%] px-3">변경 담당자</th><th className="w-[10%] px-3">담당자 ID</th><th className="w-[10%] px-3">사업자번호</th><th className="w-[19%] px-3">변경 유형</th><th className="w-[10%] px-3">경고</th></tr></thead>
                <tbody className="divide-y divide-[#f0f2f1]">
                  {detailLoading ? <tr><td colSpan={9} className="py-20 text-center text-[#8b95a1]">배치 상세를 불러오는 중입니다.</td></tr> : detailRows.length === 0 ? <tr><td colSpan={9} className="py-20 text-center text-[#8b95a1]">조회된 변경사항이 없습니다.</td></tr> : detailRows.map((row) => (
                    <tr key={row.key} className="min-h-[50px] hover:bg-[#f9fafb]">
                      <td className="px-3 py-2 font-mono text-[#4e5968]">{row.customerCode}</td>
                      <td className="truncate px-3 py-2 font-bold text-[#191f28]" title={row.customerName}>{row.customerName}</td>
                      <td className="px-3 py-2"><span className={`rounded px-1.5 py-1 font-bold ${row.sourceStatus === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{row.sourceStatus === 'ACTIVE' ? '활성' : row.sourceStatus === 'INACTIVE' ? '비활성' : row.sourceStatus || '-'}</span></td>
                      <td className="truncate px-3 py-2" title={`${row.previousManagerName} ${row.previousManagerSourceId}`}>{row.previousManagerName !== '-' ? row.previousManagerName : row.previousManagerSourceId}</td>
                      <td className="truncate px-3 py-2 font-semibold" title={row.managerName}>{row.managerName}</td>
                      <td className="truncate px-3 py-2 font-mono" title={`${row.managerSourceId} ${row.managerCode}`}>{row.managerSourceId !== '-' ? row.managerSourceId : row.managerCode}</td>
                      <td className="px-3 py-2 font-mono">{row.businessNo}</td>
                      <td className="px-3 py-2"><ActionBadges actions={row.plannedActions} /></td>
                      <td className={`break-words px-3 py-2 ${row.warningMessage ? 'font-semibold text-amber-700' : 'text-[#8b95a1]'}`}>{row.warningMessage || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between border-t border-[#eef1f4] px-5 py-3 text-[12px]"><span className="text-[#8b95a1]">총 {detailTotal.toLocaleString('ko-KR')}행 · {includeNoChange ? '변경 없음 포함' : '변경 행만 표시'}</span><div className="flex items-center gap-2"><button type="button" disabled={detailPage <= 1 || detailLoading} onClick={() => setDetailPage((value) => value - 1)} className="rounded-lg border border-[#dfe3e8] p-2 disabled:opacity-40" aria-label="이전 상세 페이지"><ChevronLeft size={15} /></button><span>{detailPage} / {detailLastPage}</span><button type="button" disabled={detailPage >= detailLastPage || detailLoading} onClick={() => setDetailPage((value) => value + 1)} className="rounded-lg border border-[#dfe3e8] p-2 disabled:opacity-40" aria-label="다음 상세 페이지"><ChevronRight size={15} /></button></div></div>
          </section>
        )}

        <section className="overflow-hidden rounded-xl border border-[#dfe5e1] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[#eef1f4] px-5 py-4"><div className="flex items-center gap-2"><History size={17} className="text-[var(--brand-primary)]" /><h2 className="text-[15px] font-bold text-[#191f28]">이전 동기화 이력</h2><span className="text-[11px] text-[#8b95a1]">총 {historyTotal.toLocaleString('ko-KR')}건</span></div><div className="flex gap-2"><select value={historyStatus} onChange={(event) => { setHistoryStatus(event.target.value); setHistoryPage(1); }} className="h-9 rounded-lg border border-[#dfe3e8] bg-white px-2 text-[12px]"><option value="">상태 전체</option>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select><button type="button" onClick={() => setHistoryRefreshKey((value) => value + 1)} disabled={historyLoading} className="flex h-9 items-center gap-1.5 rounded-lg border border-[#dfe3e8] px-3 text-[12px] font-semibold text-[#4e5968] disabled:opacity-40"><RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''} />새로고침</button></div></div>
          <div className="max-h-[360px] min-h-[220px] overflow-auto">
            <table className="w-full min-w-[1100px] table-fixed text-left text-[11px]">
              <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]"><tr className="h-10 border-b border-[#e5e8eb]"><th className="w-[8%] px-3">Batch</th><th className="w-[10%] px-3">상태</th><th className="w-[15%] px-3">생성 시각</th><th className="w-[12%] px-3">생성 관리자</th><th className="w-[15%] px-3">적용 시각</th><th className="w-[12%] px-3">적용 관리자</th><th className="w-[28%] px-3">요약</th></tr></thead>
              <tbody className="divide-y divide-[#f0f2f1]">
                {historyLoading ? <tr><td colSpan={7} className="py-16 text-center text-[#8b95a1]">동기화 이력을 불러오는 중입니다.</td></tr> : historyRows.length === 0 ? <tr><td colSpan={7} className="py-16 text-center text-[#8b95a1]">동기화 이력이 없습니다.</td></tr> : historyRows.map((batch) => (
                  <tr key={batch.batchId} onClick={() => selectBatch(batch)} className={`h-[52px] cursor-pointer hover:bg-[#f9fafb] ${String(activeBatchId) === String(batch.batchId) ? 'bg-[var(--brand-primary-soft)]/50' : ''}`}><td className="px-3 font-bold text-[#191f28]">#{batch.batchId}</td><td className="px-3"><StatusBadge status={batch.status} /></td><td className="whitespace-nowrap px-3 tabular-nums text-[#4e5968]">{formatKst(batch.createdAt)}</td><td className="truncate px-3">{batch.createdBy || '-'}</td><td className="whitespace-nowrap px-3 tabular-nums text-[#4e5968]">{formatKst(batch.appliedAt)}</td><td className="truncate px-3">{batch.appliedBy || '-'}</td><td className="truncate px-3 text-[#6b7684]" title={`변경 ${batch.summary.changedRows}행, 변경 없음 ${batch.summary.unchangedRows}행, 경고 ${batch.summary.warnings}건`}>변경 {batch.summary.changedRows.toLocaleString('ko-KR')}행 · 변경 없음 {batch.summary.unchangedRows.toLocaleString('ko-KR')}행 · 경고 {batch.summary.warnings.toLocaleString('ko-KR')}건</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-center gap-3 border-t border-[#eef1f4] px-5 py-3 text-[12px]"><button type="button" disabled={historyPage <= 1 || historyLoading} onClick={() => setHistoryPage((value) => value - 1)} className="rounded-lg border border-[#dfe3e8] px-3 py-1.5 disabled:opacity-40">이전</button><span>{historyPage} / {historyLastPage}</span><button type="button" disabled={historyPage >= historyLastPage || historyLoading} onClick={() => setHistoryPage((value) => value + 1)} className="rounded-lg border border-[#dfe3e8] px-3 py-1.5 disabled:opacity-40">다음</button></div>
        </section>
      </div>

      {confirmMode && <ConfirmationModal mode={confirmMode} batch={activeBatch} working={mutation !== null} onClose={() => { if (!mutation) setConfirmMode(null); }} onConfirm={handleMutation} />}
    </div>
  );
}
